/**
 * FERP Portal - Search History Manager
 *
 * LocalStorage-based search history tracking for global search (Cmd+K).
 * Stores last 10 searches per user with result counts and timestamps.
 *
 * FERP Principles:
 * - Client-side only (no backend storage needed)
 * - Privacy-aware (localStorage per browser)
 * - Performance: O(1) reads, O(n) dedup where n ≤ 10
 */

const STORAGE_KEY = 'ferp:search:history';
const MAX_HISTORY = 10;

export interface SearchHistoryItem {
  query: string;
  timestamp: string;
  resultCount: number;
}

/**
 * Get search history from localStorage (most recent first)
 */
export function getSearchHistory(): SearchHistoryItem[] {
  try {
    const json = localStorage.getItem(STORAGE_KEY);
    return json ? JSON.parse(json) : [];
  } catch (error) {
    console.warn('[SearchHistory] Failed to parse history:', error);
    return [];
  }
}

/**
 * Add a search to history (deduplicates and limits to MAX_HISTORY)
 */
export function addToSearchHistory(item: SearchHistoryItem): void {
  try {
    const history = getSearchHistory();

    // Remove duplicates (case-insensitive)
    const filtered = history.filter(
      (h) => h.query.toLowerCase() !== item.query.toLowerCase()
    );

    // Add to front and limit to MAX_HISTORY
    const updated = [item, ...filtered].slice(0, MAX_HISTORY);

    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch (error) {
    console.warn('[SearchHistory] Failed to save history:', error);
  }
}

/**
 * Clear all search history
 */
export function clearSearchHistory(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn('[SearchHistory] Failed to clear history:', error);
  }
}

/**
 * Remove a specific query from history
 */
export function removeFromSearchHistory(query: string): void {
  try {
    const history = getSearchHistory();
    const filtered = history.filter(
      (h) => h.query.toLowerCase() !== query.toLowerCase()
    );
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
  } catch (error) {
    console.warn('[SearchHistory] Failed to remove from history:', error);
  }
}
